<template>
  <div class="register-page">
    <div class="register-container">
      <h2 class="register-title">账号注册</h2>
      <p class="register-tip">温馨提示：您注册的账号需要审核通过后方可登录。请联系客服人员</p>

      <v-form ref="form" lazy-validation v-model="formValid" @submit.prevent="register">
        <div class="form-grid">
          <!-- 左列 -->
          <div class="form-col">
            <div class="form-item">
              <label class="form-label"><span class="required">*</span>企业名称</label>
              <div class="form-field">
                <input
                  v-model="form.enterpriseName"
                  type="text"
                  class="form-input"
                  placeholder="请输入企业名称"
                  maxlength="50"
                  @input="errors.enterpriseName = ''"
                />
                <div v-if="errors.enterpriseName" class="form-error">{{ errors.enterpriseName }}</div>
              </div>
            </div>

            <div class="form-item">
              <label class="form-label"><span class="required">*</span>统一社会信用代码</label>
              <div class="form-field">
                <input
                  v-model="form.creditCode"
                  type="text"
                  class="form-input"
                  placeholder="请输入统一社会信用代码"
                  maxlength="18"
                  @input="errors.creditCode = ''"
                />
                <div v-if="errors.creditCode" class="form-error">{{ errors.creditCode }}</div>
              </div>
            </div>

            <div class="form-item">
              <label class="form-label"><span class="required">*</span>行业类别</label>
              <div class="form-field">
                <select
                  v-model="form.oneBusiness"
                  class="form-input form-select"
                  :disabled="industryLoading"
                >
                  <option value="" disabled>请选择一级行业</option>
                  <option
                    v-for="item in industryOptions"
                    :key="item.id"
                    :value="item.businessName"
                  >{{ item.businessName }}</option>
                </select>
                <div v-if="errors.oneBusiness" class="form-error">{{ errors.oneBusiness }}</div>
              </div>
            </div>

            <div class="form-item">
              <label class="form-label"><span class="required">*</span>细分行业</label>
              <div class="form-field">
                <select
                  v-model="form.twoBusiness"
                  class="form-input form-select"
                  :disabled="!form.oneBusiness || industryLoading"
                >
                  <option value="" disabled>{{ form.oneBusiness ? '请选择细分行业' : '请先选择一级行业' }}</option>
                  <option
                    v-for="item in subIndustryOptions"
                    :key="item.id"
                    :value="item.businessName"
                  >{{ item.businessName }}</option>
                </select>
                <div v-if="errors.twoBusiness" class="form-error">{{ errors.twoBusiness }}</div>
              </div>
            </div>

            <div class="form-item">
              <label class="form-label"><span class="required">*</span>企业所在省</label>
              <div class="form-field">
                <select
                  v-model="form.province"
                  class="form-input form-select"
                >
                  <option value="" disabled>请选择省份</option>
                  <option
                    v-for="item in provinceOptions"
                    :key="item.areaCode"
                    :value="item.name"
                  >{{ item.name }}</option>
                </select>
                <div v-if="errors.province" class="form-error">{{ errors.province }}</div>
              </div>
            </div>

            <div class="form-item">
              <label class="form-label"><span class="required">*</span>企业所在市</label>
              <div class="form-field">
                <select
                  v-model="form.city"
                  class="form-input form-select"
                  :disabled="!form.province"
                >
                  <option value="" disabled>{{ form.province ? '请选择城市' : '请先选择省份' }}</option>
                  <option
                    v-for="item in cityOptions"
                    :key="item.areaCode"
                    :value="item.name"
                  >{{ item.name }}</option>
                </select>
                <div v-if="errors.city" class="form-error">{{ errors.city }}</div>
              </div>
            </div>

            <div class="form-item">
              <label class="form-label"><span class="required">*</span>企业所在区（县）</label>
              <div class="form-field">
                <select
                  v-model="form.district"
                  class="form-input form-select"
                  :disabled="!form.city"
                >
                  <option value="" disabled>{{ form.city ? '请选择区（县）' : '请先选择城市' }}</option>
                  <option
                    v-for="item in districtOptions"
                    :key="item.areaCode"
                    :value="item.name"
                  >{{ item.name }}</option>
                </select>
                <div v-if="errors.district" class="form-error">{{ errors.district }}</div>
              </div>
            </div>

            <div class="form-item">
              <label class="form-label"><span class="required">*</span>详细地址</label>
              <div class="form-field">
                <input
                  v-model="form.enterpriseAllAddress"
                  type="text"
                  class="form-input"
                  placeholder="请输入详细地址"
                  maxlength="100"
                  @input="errors.enterpriseAllAddress = ''"
                />
                <div v-if="errors.enterpriseAllAddress" class="form-error">{{ errors.enterpriseAllAddress }}</div>
              </div>
            </div>
          </div>

          <!-- 右列 -->
          <div class="form-col">
            <div class="form-item">
              <label class="form-label"><span class="required">*</span>联系人姓名</label>
              <div class="form-field">
                <input
                  v-model="form.name"
                  type="text"
                  class="form-input"
                  placeholder="请输入联系人姓名"
                  maxlength="20"
                  @input="errors.name = ''"
                />
                <div v-if="errors.name" class="form-error">{{ errors.name }}</div>
              </div>
            </div>

            <div class="form-item">
              <label class="form-label"><span class="required">*</span>联系人电话</label>
              <div class="form-field">
                <input
                  v-model="form.phone"
                  type="text"
                  class="form-input"
                  placeholder="请输入联系人电话"
                  maxlength="11"
                  @input="errors.phone = ''"
                  @blur="validatePhone"
                />
                <div v-if="errors.phone" class="form-error">{{ errors.phone }}</div>
              </div>
            </div>

            <div class="form-item">
              <label class="form-label"><span class="required">*</span>联系人邮箱</label>
              <div class="form-field">
                <input
                  v-model="form.email"
                  type="email"
                  class="form-input"
                  placeholder="请输入联系人邮箱"
                  maxlength="50"
                  @input="errors.email = ''"
                  @blur="validateEmail"
                />
                <div v-if="errors.email" class="form-error">{{ errors.email }}</div>
              </div>
            </div>

            <div class="form-item">
              <label class="form-label"><span class="required">*</span>登录账号</label>
              <div class="form-field">
                <input
                  v-model="form.loginAccount"
                  type="text"
                  class="form-input"
                  placeholder="请输入手机号"
                  maxlength="11"
                  @input="errors.loginAccount = ''"
                />
                <div v-if="errors.loginAccount" class="form-error">{{ errors.loginAccount }}</div>
              </div>
            </div>

            <div class="form-item">
              <label class="form-label"><span class="required">*</span>登录密码</label>
              <div class="form-field">
                <div class="form-input-wrap">
                  <input
                    v-model="form.password"
                    :type="showPassword ? 'text' : 'password'"
                    class="form-input"
                    placeholder="请输入登录密码"
                    maxlength="20"
                    @input="errors.password = ''"
                  />
                  <span class="form-toggle" @click="showPassword = !showPassword">
                    {{ showPassword ? '隐藏' : '显示' }}
                  </span>
                </div>
                <div v-if="errors.password" class="form-error">{{ errors.password }}</div>
              </div>
            </div>

            <div class="form-item">
              <label class="form-label"><span class="required">*</span>上传营业执照</label>
              <div class="form-field">
                <div class="upload-area" @click="triggerUpload">
                  <input
                    ref="fileInput"
                    type="file"
                    accept="image/*"
                    style="display: none"
                    @change="handleFileChange"
                  />
                  <div v-if="!form.businessLicense" class="upload-placeholder">
                    <div class="upload-icon">+</div>
                    <div class="upload-text">上传图片</div>
                  </div>
                  <div v-else class="upload-preview">
                    <img :src="form.businessLicense" alt="营业执照" />
                    <div class="upload-actions">
                      <span @click.stop="previewImage">查看</span>
                      <span @click.stop="removeImage">删除</span>
                    </div>
                  </div>
                </div>
                <div v-if="errors.businessLicense" class="form-error">{{ errors.businessLicense }}</div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="errorMsg" class="register-error">{{ errorMsg }}</div>

        <div class="form-actions">
          <button class="btn btn-primary" :disabled="loading" @click="register">
            <span v-if="!loading">注 册</span>
            <span v-else>注册中...</span>
          </button>
          <button class="btn btn-secondary" @click="goLogin">返回登录</button>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script>
import validate from "@/assets/js/validate";
import cityData from "@/assets/js/city";
import { cusRegister, productUploadFile, queryBusinessList } from "@/api/travelInsurance";

export default {
  name: "RegisterPage",
  data() {
    return {
      form: {
        enterpriseName: "",
        creditCode: "",
        oneBusiness: "",
        twoBusiness: "",
        province: "",
        provinceCode: "",
        city: "",
        cityCode: "",
        district: "",
        districtCode: "",
        enterpriseAllAddress: "",
        businessLicense: "",
        name: "",
        phone: "",
        email: "",
        loginAccount: "",
        password: ""
      },
      errors: {
        enterpriseName: "",
        creditCode: "",
        oneBusiness: "",
        twoBusiness: "",
        province: "",
        city: "",
        district: "",
        enterpriseAllAddress: "",
        businessLicense: "",
        name: "",
        phone: "",
        email: "",
        loginAccount: "",
        password: ""
      },
      formValid: true,
      loading: false,
      showPassword: false,
      errorMsg: "",
      industryOptions: [],
      subIndustryOptions: [],
      industryLoading: false,
      provinceOptions: cityData || [],
      cityOptions: [],
      districtOptions: []
    };
  },
  mounted() {
    this.loadBusinessList(0);
  },
  watch: {
    "form.oneBusiness"(val) {
      this.form.twoBusiness = "";
      this.subIndustryOptions = [];
      this.errors.twoBusiness = "";
      if (!val) return;
      const parent = this.industryOptions.find(it => it.businessName === val);
      if (parent) {
        this.loadBusinessList(parent.dictCode);
      }
    },
    "form.province"(val) {
      // 选择省：清空市/区及编码，加载该省下的市
      this.form.provinceCode = "";
      this.form.city = "";
      this.form.cityCode = "";
      this.form.district = "";
      this.form.districtCode = "";
      this.cityOptions = [];
      this.districtOptions = [];
      this.errors.city = "";
      this.errors.district = "";
      if (!val) return;
      const prov = this.provinceOptions.find(it => it.name === val);
      if (prov) {
        this.form.provinceCode = prov.areaCode;
        this.cityOptions = prov.children || [];
      }
    },
    "form.city"(val) {
      // 选择市：清空区及编码，加载该市下的区
      this.form.cityCode = "";
      this.form.district = "";
      this.form.districtCode = "";
      this.districtOptions = [];
      this.errors.district = "";
      if (!val) return;
      const city = this.cityOptions.find(it => it.name === val);
      if (city) {
        this.form.cityCode = city.areaCode;
        this.districtOptions = city.children || [];
      }
    },
    "form.district"(val) {
      if (!val) {
        this.form.districtCode = "";
        return;
      }
      const dist = this.districtOptions.find(it => it.name === val);
      this.form.districtCode = dist ? dist.areaCode : "";
    }
  },
  methods: {
    // 加载行业类别列表（一级 fatherCode=0；二级传选中一级的 dictCode）
    async loadBusinessList(fatherCode) {
      this.industryLoading = true;
      try {
        const res = await queryBusinessList(fatherCode);
        const list = (res && (res.data || res)) || [];
        if (Number(fatherCode) === 0 || fatherCode === 0) {
          this.industryOptions = list;
        } else {
          this.subIndustryOptions = list;
        }
      } catch (e) {
        if (Number(fatherCode) === 0 || fatherCode === 0) {
          this.industryOptions = [];
        } else {
          this.subIndustryOptions = [];
        }
      } finally {
        this.industryLoading = false;
      }
    },
    validatePhone() {
      this.errors.phone = "";
      if (!this.form.phone) {
        this.errors.phone = "请输入联系人电话";
        return false;
      }
      if (!validate.isMobilePhone(this.form.phone)) {
        this.errors.phone = "请输入正确的11位手机号";
        return false;
      }
      return true;
    },
    validateEmail() {
      this.errors.email = "";
      if (!this.form.email) {
        this.errors.email = "请输入联系人邮箱";
        return false;
      }
      if (!validate.isEmail(this.form.email)) {
        this.errors.email = "请输入正确的邮箱格式";
        return false;
      }
      return true;
    },
    validateForm() {
      Object.keys(this.errors).forEach(k => (this.errors[k] = ""));
      let valid = true;

      if (!this.form.enterpriseName) {
        this.errors.enterpriseName = "请输入企业名称";
        valid = false;
      }
      if (!this.form.creditCode) {
        this.errors.creditCode = "请输入统一社会信用代码";
        valid = false;
      } else if (!validate.isUnifiedSocialCreditCode(this.form.creditCode)) {
        this.errors.creditCode = "请输入正确的统一社会信用代码";
        valid = false;
      }
      if (!this.form.oneBusiness) {
        this.errors.oneBusiness = "请选择行业类别";
        valid = false;
      }
      if (!this.form.twoBusiness) {
        this.errors.twoBusiness = "请选择细分行业";
        valid = false;
      }
      if (!this.form.province) {
        this.errors.province = "请选择企业所在省";
        valid = false;
      }
      if (!this.form.city) {
        this.errors.city = "请选择企业所在市";
        valid = false;
      }
      if (!this.form.district) {
        this.errors.district = "请选择企业所在区（县）";
        valid = false;
      }
      if (!this.form.enterpriseAllAddress) {
        this.errors.enterpriseAllAddress = "请输入详细地址";
        valid = false;
      }
      if (!this.form.businessLicense) {
        this.errors.businessLicense = "请上传营业执照";
        valid = false;
      }
      if (!this.form.name) {
        this.errors.name = "请输入联系人姓名";
        valid = false;
      }
      if (!this.form.phone) {
        this.errors.phone = "请输入联系人电话";
        valid = false;
      } else if (!validate.isMobilePhone(this.form.phone)) {
        this.errors.phone = "请输入正确的11位手机号";
        valid = false;
      }
      if (!this.form.email) {
        this.errors.email = "请输入联系人邮箱";
        valid = false;
      } else if (!validate.isEmail(this.form.email)) {
        this.errors.email = "请输入正确的邮箱格式";
        valid = false;
      }
      if (!this.form.loginAccount) {
        this.errors.loginAccount = "请输入登录账号";
        valid = false;
      } else if (!validate.isMobilePhone(this.form.loginAccount)) {
        this.errors.loginAccount = "请输入正确的手机号";
        valid = false;
      }
      if (!this.form.password) {
        this.errors.password = "请输入登录密码";
        valid = false;
      } else if (!validate.isPassword(this.form.password)) {
        this.errors.password = "密码格式错误，只能包含6-20位数字、字母、下划线";
        valid = false;
      }

      return valid;
    },
    triggerUpload() {
      this.$refs.fileInput.click();
    },
    handleFileChange(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      if (!/^image\//.test(file.type)) {
        this.$message.error("只能上传图片文件");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$message.error("图片大小不能超过 5MB");
        return;
      }
      productUploadFile('img',file)
        .then(res => {
          if (res && res.code === 0) {
            this.form.businessLicense = res.data;
          } else {
            this.$message.error(res && res.msg ? res.msg : "上传失败");
          }
        })
        .catch(() => {
          this.$message.error("上传失败，请重试");
        });
    },
    previewImage() {
      window.open(this.form.businessLicense, "_blank");
    },
    removeImage() {
      this.form.businessLicense = "";
      this.$refs.fileInput.value = "";
    },
    register() {
      if (this.loading) return;
      if (!this.validateForm()) return;
      this.errorMsg = "";
      this.loading = true;
      cusRegister(this.form)
        .then(res => {
          this.loading = false;
          if (res.code === 0) {
            this.$message.success("注册成功，请登录");
            this.goLogin();
          } else {
            this.errorMsg = res.msg || "注册失败，请重试";
          }
        })
        .catch(err => {
          this.loading = false;
          this.errorMsg = (err && err.msg) || "注册失败，请重试";
        });
    },
    goLogin() {
      const redirect = this.$route.query.redirect;
      this.$router.push(
        redirect ? { path: "/login", query: { redirect } } : { path: "/login" }
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.register-page {
  width: 100%;
  min-height: 100vh;
  background: #fff;
  padding: 30px 20px 60px;
  box-sizing: border-box;
}

.register-container {
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
}

.register-title {
  font-size: 28px;
  font-weight: 600;
  color: #303133;
  text-align: center;
  margin: 0 0 12px;
}

.register-tip {
  text-align: center;
  font-size: 15px;
  font-weight: 500;
  color: #dd122a;
  margin: 0 0 28px;
  padding: 8px 16px;
  background: #fff5f5;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
}

.form-grid {
  display: flex;
  gap: 36px;
}

.form-col {
  flex: 1;
  min-width: 0;
}

.form-item {
  margin-bottom: 18px;
  position: relative;
  display: flex;
  align-items: flex-start;
}

.form-label {
  flex-shrink: 0;
  width: 120px;
  margin-right: 12px;
  font-size: 14px;
  color: #606266;
  text-align: right;
  line-height: 1.4;
  white-space: nowrap;

  &::after {
    content: "：";
  }

  .required {
    color: #dd122a;
    margin-right: 2px;
  }
}

.form-field {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.form-input {
  width: 100%;
  height: 38px;
  padding: 0 14px;
  font-size: 14px;
  color: #303133;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    border-color: #dd122a;
  }
  &::placeholder {
    color: #c0c4cc;
  }
}

.form-select {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23999999' d='M2 4l4 4 4-4z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 12px;
  padding-right: 32px;
}

.form-input-wrap {
  position: relative;
}

.form-input-wrap .form-input {
  padding-right: 50px;
}

.form-toggle {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: #dd122a;
  cursor: pointer;
  user-select: none;
  line-height: 1;
  z-index: 2;
}

.form-error {
  margin-top: 4px;
  font-size: 12px;
  color: #f56c6c;
  line-height: 1.4;
}

.register-error {
  margin: 8px 0 12px;
  padding: 8px 12px;
  background: #fef0f0;
  color: #f56c6c;
  font-size: 13px;
  border-radius: 4px;
  text-align: center;
}

.upload-area {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 110px;
  height: 110px;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.2s;
  vertical-align: top;

  &:hover {
    border-color: #dd122a;
  }
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #909399;
  font-size: 13px;

  .upload-icon {
    font-size: 28px;
    line-height: 1;
    margin-bottom: 4px;
  }
}

.upload-preview {
  width: 100%;
  height: 100%;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .upload-actions {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 12px;
    display: flex;
    justify-content: space-around;
    padding: 4px 0;
    cursor: default;

    span {
      cursor: pointer;
      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.form-actions {
  margin-top: 24px;
  display: flex;
  justify-content: center;
  gap: 16px;
}

.btn {
  min-width: 160px;
  height: 40px;
  padding: 0 28px;
  font-size: 15px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s;
}

.btn-primary {
  background: #dd122a;
  color: #fff;
  border-color: #dd122a;

  &:hover:not(:disabled) {
    background: #c10f24;
    border-color: #c10f24;
  }
  &:disabled {
    background: #f0a5ad;
    border-color: #f0a5ad;
    cursor: not-allowed;
  }
}

.btn-secondary {
  background: #fff;
  color: #606266;
  border-color: #dcdfe6;

  &:hover {
    color: #dd122a;
    border-color: #dd122a;
  }
}

@media (max-width: 768px) {
  .form-grid {
    flex-direction: column;
    gap: 0;
  }
  .form-actions {
    flex-direction: column;
    align-items: stretch;
  }
  .btn {
    width: 100%;
  }
}
</style>
